import { useMemo, forwardRef, useId } from 'react';
import { Box } from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';

import { RenderComponentProps } from '../@types';
import i18n from '../translations/i18n';
import { nameToClass } from '../utils/css';
import { createLogLocal } from '../utils/createLogLocal';

import { InputControl } from './ui/controls/InputControl';

type TransformerResponse = {
  Component: React.FC<any>;
  props: Record<string, any>;
};

const log = createLogLocal('RenderComponent');

/**
 * Transforms field and config data into component and props for rendering.
 * "Component" is the React component to render.
 * "props" are the props to pass to that component.
 *
 * Example:
 * The cardNumber field has component TokenexIFrameControl, & props including
 * id="tokenexCardNumber", label="cardNumber", name="cardNumber", etc
 */
export const transformDataForRenderComponent = ({
  field,
  config,
}: RenderComponentProps): TransformerResponse | null => {
  if (!field || !config) return null;

  const t = i18n.t;
  const { component, label, id, name, defaultValue, list, className, scheme } =
    field;

  const { required: isRequired = false } = config || {};

  const Component = component || InputControl;

  const props = {
    isRequired,
    label: t(label),
    name,
    ...(id && { id }),
    ...(defaultValue && { defaultValue }),
    ...(className && { className }),
    ...(list && { list }),
    ...(scheme && { scheme }),
  };

  return { Component, props };
};

export const RenderComponent = forwardRef<any, any>(
  ({ field, config, ...renderComponentProps }: RenderComponentProps, ref) => {
    log('RenderComponent', 'field', field, 'config:', config);
    log('RenderComponent', 'renderComponentProps', renderComponentProps);

    const key = useId();

    const { Component, props } = useMemo(
      () =>
        transformDataForRenderComponent({ field, config }) ||
        ({} as TransformerResponse),
      [field, config],
    );

    if (!Component || !props || !field) return null;

    const { className = 'LongTextControl', ...otherProps } = props;

    return (
      <Box
        className={cx(
          'FormControlContainer',
          nameToClass(field.name, 'container'),
          className,
        )}
      >
        {/* Render the input component e.g. TokenexIFrameControl. Default: InputControl */}
        <Component
          key={key}
          ref={ref}
          {...otherProps}
          {...renderComponentProps}
        />
      </Box>
    );
  },
);
